import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import Handler from "../../Helpers/Handler";
import ApiCall from "../../Helpers/ApiCall";
import MainLayout from "../../Partials/MainLayout";
import FeedLayout from "../../Partials/FeedLayout";
import "../../Assets/css/style.css";

const BASE_URL = "https://qnexus.in/community/";
// const BASE_URL = "http://localhost/business_community/";
const FALLBACK = "https://ui-avatars.com/api/?background=4f46e5&color=fff&name=";
const POLL_MS = 5000;

/* ── helpers ────────────────────────────────── */
const formatTime = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleTimeString("en-IN", {
        hour: "2-digit", minute: "2-digit", hour12: true,
    });
};

const formatDay = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now - d) / 86400000);
    if (diff === 0) return "Today";
    if (diff === 1) return "Yesterday";
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" });
};

const dayKey = (dateStr) =>
    dateStr ? new Date(dateStr).toDateString() : "unknown";

/* ══════════════════════════════════════════════
   Chat component
══════════════════════════════════════════════ */
const Chat = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { chat_id, other_user } = location.state || {};

    const currentUser = useSelector((s) => s.customer?.customer?.user);

    /* ─── state ─── */
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [text, setText] = useState("");
    const [error, setError] = useState("");
    const [isTyping, setIsTyping] = useState(false);
    const [onlineStatus, setOnlineStatus] = useState({ online: false, last_seen: null });

    /* Delete confirmation state */
    const [deleteMenu, setDeleteMenu] = useState({ show: false, msgId: null, isMine: false, x: 0, y: 0 });

    /* ─── refs ─── */
    const bottomRef = useRef(null);
    const fileRef = useRef(null);
    const pollRef = useRef(null);
    const typingTimer = useRef(null);
    const deleteMenuRef = useRef(null);

    /* ─── callbacks ─── */
    const fetchMessages = useCallback(async (silent = false) => {
        if (!chat_id) return;
        if (!silent) setLoading(true);
        const res = await Handler({ method: "get", url: `/chat/get_messages.php?chat_id=${chat_id}` });
        if (!silent) setLoading(false);
        if (res?.success) setMessages(res.data?.messages || []);
    }, [chat_id]);

    const markRead = useCallback(async () => {
        if (!chat_id) return;
        await Handler({ method: "post", url: "/chat/mark_read.php", data: { chat_id } });
    }, [chat_id]);

    const fetchOnline = useCallback(async () => {
        if (!chat_id || !other_user?.user_id) return;
        const res = await Handler({
            method: "get",
            url: `/chat/last_seen.php?chat_id=${chat_id}&user_id=${other_user.user_id}`,
        });
        if (res?.success) {
            setOnlineStatus({ online: res.data.online, last_seen: res.data.last_seen });
        }
    }, [chat_id, other_user?.user_id]);

    const fetchTyping = useCallback(async () => {
        if (!chat_id) return;
        const res = await Handler({
            method: "post",
            url: "/chat/typing_status.php",
            data: { chat_id },
        });
        if (res?.success) setIsTyping(res.data?.typing === true);
    }, [chat_id]);

    const emitTyping = useCallback(async () => {
        if (!chat_id) return;
        await Handler({ method: "post", url: "/chat/typing.php", data: { chat_id } });
    }, [chat_id]);

    /* ─── Close delete menu on outside click ─── */
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (deleteMenuRef.current && !deleteMenuRef.current.contains(e.target)) {
                setDeleteMenu({ show: false, msgId: null, isMine: false, x: 0, y: 0 });
            }
        };
        if (deleteMenu.show) {
            document.addEventListener("mousedown", handleClickOutside);
            document.addEventListener("touchstart", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("touchstart", handleClickOutside);
        };
    }, [deleteMenu.show]);

    /* ─── effects ─── */
    useEffect(() => {
        if (!chat_id) return;
        fetchMessages();
        markRead();
        fetchOnline();

        pollRef.current = setInterval(() => {
            fetchMessages(true);
            fetchTyping();
            fetchOnline();
        }, POLL_MS);

        return () => clearInterval(pollRef.current);
    }, [fetchMessages, markRead, fetchOnline, fetchTyping, chat_id]);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, isTyping]);

    /* ─── GUARD ─── */
    if (!chat_id) {
        return (
            <MainLayout>
                <FeedLayout>
                    <div className="ch-guard">
                        <i className="bx bx-error-circle" />
                        <p>Invalid chat. Please go back and try again.</p>
                        <button onClick={() => navigate("/user/chat-list")}>← Back to Chats</button>
                    </div>
                </FeedLayout>
            </MainLayout>
        );
    }

    /* ─── event handlers ─── */
    const sendText = async () => {
        const msg = text.trim();
        if (!msg) return;
        setSending(true);
        setError("");
        const res = await Handler({
            method: "post",
            url: "/chat/send.php",
            data: { chat_id, message: msg, message_type: "TEXT" },
        });
        setSending(false);
        if (res?.success) {
            setText("");
            await fetchMessages(true);
        } else {
            setError(res?.message || "Failed to send message.");
        }
    };

    const sendFile = async (file, type = "IMAGE") => {
        if (!file) return;
        setSending(true);
        setError("");
        try {
            const formData = new FormData();
            formData.append("chat_id", chat_id);
            formData.append("file", file);
            formData.append("type", type);
            const res = await ApiCall({
                url: "/chat/message.php",
                method: "POST",
                data: formData,
                headers: { "Content-Type": "multipart/form-data" },
            });
            if (res?.success) {
                await fetchMessages(true);
            } else {
                setError(res?.message || "Failed to send file.");
            }
        } catch {
            setError("File send failed.");
        }
        setSending(false);
    };

    const deleteMessage = async (msgId, forEveryone = false) => {
        const res = await Handler({
            method: "post",
            url: "/chat/delete.php",
            data: {
                action: forEveryone ? "delete_for_everyone" : "delete_message",
                message_id: msgId,
            },
        });
        if (res?.success) {
            setMessages((prev) => prev.filter((m) => m.id !== msgId));
        } else {
            setError(res?.message || "Delete failed.");
        }
        setDeleteMenu({ show: false, msgId: null, isMine: false, x: 0, y: 0 });
    };

    const handleDeleteClick = (e, msgId, isMine) => {
        e.stopPropagation();
        const rect = e.currentTarget.getBoundingClientRect();

        /* Menu dimensions */
        const menuHeight = isMine ? 160 : 110;
        const menuWidth = 240;

        /* Available space */
        const spaceBelow = window.innerHeight - rect.bottom - 10;
        const spaceAbove = rect.top - 10;

        /* Force menu to open ABOVE the message if not enough space below */
        let y;
        if (spaceBelow < menuHeight && spaceAbove > spaceBelow) {
            y = rect.top - menuHeight - 6;
        } else {
            y = rect.bottom + 6;
        }

        /* X position */
        let x = isMine ? (rect.right - menuWidth) : rect.left;

        /* Keep inside screen bounds */
        if (x + menuWidth > window.innerWidth - 10) x = window.innerWidth - menuWidth - 10;
        if (x < 10) x = 10;
        if (y < 10) y = 10;

        setDeleteMenu({ show: true, msgId, isMine, x, y });
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            sendText();
        }
    };

    const handleTextChange = (e) => {
        setText(e.target.value);
        emitTyping();
        clearTimeout(typingTimer.current);
        typingTimer.current = setTimeout(() => { }, 3000);
    };

    /* ─── derived values ─── */
    const otherAvatar = other_user?.logo
        ? BASE_URL + other_user.logo
        : `${FALLBACK}${encodeURIComponent(other_user?.name || "U")}`;

    const grouped = messages.reduce((acc, msg) => {
        if (msg.deleted_for_everyone) return acc;
        const isMine = msg.sender_id === currentUser?.id;
        if (isMine && msg.deleted_by_sender) return acc;
        if (!isMine && msg.deleted_by_receiver) return acc;

        const key = dayKey(msg.created_at);
        if (!acc[key]) acc[key] = { label: formatDay(msg.created_at), msgs: [] };
        acc[key].msgs.push(msg);
        return acc;
    }, {});

    /* ─── render ─── */
    return (
        <MainLayout>
            <FeedLayout>
                <div className="ch-page">

                    {/* Header */}
                    <div className="ch-header">
                        <button className="ch-back" onClick={() => navigate("/user/chat-list")}>
                            <i className="bx bx-arrow-back" />
                        </button>

                        <img
                            className="ch-peer-avatar"
                            src={otherAvatar}
                            alt={other_user?.name}
                            onError={(e) => {
                                e.target.src = `${FALLBACK}${encodeURIComponent(other_user?.name || "U")}`;
                            }}
                        />

                        <div className="ch-peer-info">
                            <div className="ch-peer-name">
                                {other_user?.name || "Unknown"}
                                {other_user?.is_verified && (
                                    <i className="bx bxs-badge-check ch-tick" title="Verified" />
                                )}
                            </div>
                            <div className={`ch-status ${onlineStatus.online ? "online" : "offline"}`}>
                                {onlineStatus.online
                                    ? "Online"
                                    : onlineStatus.last_seen
                                        ? `Last seen ${formatTime(onlineStatus.last_seen)}`
                                        : "Offline"}
                            </div>
                        </div>
                    </div>

                    {/* Error */}
                    {error && (
                        <div className="ch-error">
                            <i className="bx bx-error-circle" />
                            {error}
                        </div>
                    )}

                    {/* Messages */}
                    <div className="ch-messages">
                        {loading ? (
                            <div className="ch-loading">
                                <i className="bx bx-loader-alt bx-spin ch-spinner" />
                                <span>Loading messages…</span>
                            </div>
                        ) : Object.keys(grouped).length === 0 ? (
                            <div className="ch-empty">
                                <i className="bx bx-chat" />
                                <p>No messages yet. Say hello!</p>
                            </div>
                        ) : (
                            Object.values(grouped).map((group) => (
                                <div key={group.label}>
                                    <div className="ch-day-divider">
                                        <span>{group.label}</span>
                                    </div>

                                    {group.msgs.map((msg) => {
                                        const isMine = msg.sender_id === currentUser?.id;
                                        return (
                                            <div
                                                key={msg.id}
                                                className={`ch-bubble-wrap ${isMine ? "mine" : "theirs"}`}
                                            >
                                                {/* Other user avatar */}
                                                {!isMine && (
                                                    <img
                                                        className="ch-bubble-avatar"
                                                        src={otherAvatar}
                                                        alt={other_user?.name}
                                                        onError={(e) => {
                                                            e.target.src = `${FALLBACK}${encodeURIComponent(other_user?.name || "U")}`;
                                                        }}
                                                    />
                                                )}

                                                {/* Bubble + actions wrapper */}
                                                <div className="ch-bubble-group">

                                                    {/* Single delete button — visible on hover */}
                                                    <div className="ch-msg-actions">
                                                        <button
                                                            className="ch-del-btn"
                                                            title="Delete message"
                                                            onClick={(e) => handleDeleteClick(e, msg.id, isMine)}
                                                        >
                                                            <i className="bx bx-trash" />
                                                        </button>
                                                    </div>

                                                    {/* Message bubble */}
                                                    <div className="ch-bubble">
                                                        {msg.message_type === "IMAGE" && (
                                                            <a
                                                                href={BASE_URL + msg.file_url}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                className="ch-bubble-img-link" /* Optional class if needed */
                                                            >
                                                                <img
                                                                    className="ch-bubble-img"
                                                                    src={BASE_URL + msg.file_url}
                                                                    alt="Shared content"
                                                                    loading="lazy"
                                                                    onError={(e) => {
                                                                        // Hide image if broken to preserve layout
                                                                        e.target.style.display = "none";
                                                                        e.target.parentElement.style.display = "none";
                                                                    }}
                                                                />
                                                            </a>
                                                        )}



                                                        {msg.message_type === "FILE" && (
                                                            <a
                                                                className="ch-file-link"
                                                                href={BASE_URL + msg.file_url}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                            >
                                                                <i className="bx bx-file" />
                                                                <span>{msg.file_name || "Download file"}</span>
                                                            </a>
                                                        )}

                                                        {msg.message_type === "TEXT" && (
                                                            <p className="ch-bubble-text">{msg.message}</p>
                                                        )}

                                                        <div className="ch-bubble-meta">
                                                            <span className="ch-bubble-time">{formatTime(msg.created_at)}</span>
                                                            {isMine && (
                                                                <span className={`ch-tick-icon ${msg.is_read ? "read" : ""}`}>
                                                                    <i className={`bx ${msg.is_read ? "bx-check-double" : "bx-check"}`} />
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>

                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ))
                        )}

                        {/* Typing indicator */}
                        {isTyping && (
                            <div className="ch-bubble-wrap theirs">
                                <div className="ch-bubble ch-typing">
                                    <span /><span /><span />
                                </div>
                            </div>
                        )}

                        <div ref={bottomRef} />
                    </div>

                    {/* Delete confirmation menu - Placed OUTSIDE .ch-messages to prevent clipping */}
                    {deleteMenu.show && (
                        <div
                            ref={deleteMenuRef}
                            className="ch-delete-menu"
                            style={{ top: deleteMenu.y, left: deleteMenu.x }}
                        >
                            <div className="ch-delete-menu-header">Delete Message</div>
                            <button
                                className="ch-delete-opt"
                                onClick={() => deleteMessage(deleteMenu.msgId, false)}
                            >
                                <i className="bx bx-trash" />
                                <div>
                                    <span className="ch-delete-opt-title">Delete for me</span>
                                    <span className="ch-delete-opt-desc">Remove from your view only</span>
                                </div>
                            </button>

                            {deleteMenu.isMine && (
                                <button
                                    className="ch-delete-opt ch-delete-opt-danger"
                                    onClick={() => deleteMessage(deleteMenu.msgId, true)}
                                >
                                    <i className="bx bx-message-x" />
                                    <div>
                                        <span className="ch-delete-opt-title">Delete for everyone</span>
                                        <span className="ch-delete-opt-desc">Permanently remove for both users</span>
                                    </div>
                                </button>
                            )}
                        </div>
                    )}

                    {/* Input bar */}

                    {/* Input bar */}
                    <div className="ch-input-bar">

                        {/* ✅ Hidden file input — moved OUTSIDE the button */}
                        <input
                            ref={fileRef}
                            type="file"
                            style={{ display: "none" }}
                            accept="image/jpeg,image/png,image/webp,image/jpg,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/zip,text/plain"
                            onChange={(e) => {
                                const file = e.target.files[0];
                                if (!file) return;
                                const imageExts = ['jpg', 'jpeg', 'png', 'webp'];
                                const ext = file.name.split('.').pop().toLowerCase();
                                const type = imageExts.includes(ext) ? "IMAGE" : "FILE";
                                sendFile(file, type);
                                e.target.value = "";
                            }}
                        />

                        {/* ✅ Button now has NO children except the icon */}
                        <button
                            className="ch-icon-btn"
                            title="Send image"
                            disabled={sending}
                            onClick={() => fileRef.current?.click()}
                        >
                            <i className="bx bx-image-add" />
                        </button>

                        <input
                            className="ch-text-input"
                            type="text"
                            placeholder="Type a message…"
                            value={text}
                            onChange={handleTextChange}
                            onKeyDown={handleKeyDown}
                            disabled={sending}
                            maxLength={2000}
                        />

                        <button
                            className={`ch-send-btn ${text.trim() && !sending ? "active" : ""}`}
                            onClick={sendText}
                            disabled={sending || !text.trim()}
                            title="Send"
                        >
                            {sending
                                ? <i className="bx bx-loader-alt bx-spin" />
                                : <i className="bx bx-send" />}
                        </button>

                    </div>

                </div>
            </FeedLayout>
        </MainLayout>
    );
};

export default Chat;